import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SidebarCol } from "../../chatMain/Sidebar/Sidebar";
import { MainHeader } from "../../chatMain/MainHeader/MainHeader.jsx";
import defaultImage from "../default.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import {
  faBookmark,
  faVideo,
  faCalendar,
  faClock,
  faUsers,
  faLocationDot,
  faCheckCircle,
  faShieldHalved,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import {
  getLiveEventById,
  registerForLiveEvent,
  unregisterFromLiveEvent,
} from "../../../services/liveEventsService";
import { updateEventRegistration } from "../../../store/liveEventsSlice";
import "./LiveRegister.css";

function parseUtcDate(rawDate) {
  if (!rawDate) return null;
  if (rawDate instanceof Date) return rawDate;
  let str = String(rawDate).trim();
  if (!str.includes("Z") && !str.includes("+") && !str.match(/-\d{2}:\d{2}$/)) {
    str = str.replace(" ", "T") + "Z";
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? new Date(rawDate) : d;
}

function getJoinWindowStatus(rawDate, durationMinutes = 30) {
  if (!rawDate) return { canJoin: false, label: "Join Live Session", status: "inactive", reason: "" };
  const d = parseUtcDate(rawDate);
  if (!d || isNaN(d.getTime())) return { canJoin: false, label: "Join Live Session", status: "inactive", reason: "" };

  const now = Date.now();
  const startTime = d.getTime();
  const openTime = startTime - 60 * 60 * 1000; // 1 hour (60 mins) before
  const endTime = startTime + (durationMinutes + 15) * 60 * 1000; // 15 mins after scheduled end

  if (now < openTime) {
    const diffMs = openTime - now;
    const diffMins = Math.ceil(diffMs / (60 * 1000));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    let timingStr = "Opens 1h before";
    if (diffDays > 0) {
      timingStr = `Opens in ${diffDays}d (1h before)`;
    } else if (diffHours > 0) {
      timingStr = `Opens in ${diffHours}h (1h before)`;
    } else if (diffMins > 0) {
      timingStr = `Opens in ${diffMins}m`;
    }

    return {
      canJoin: false,
      label: `Join Live (${timingStr})`,
      reason: "Meeting link opens 1 hour before the scheduled start time.",
      status: "early",
    };
  } else if (now <= endTime) {
    if (now < startTime) {
      const diffToStartMs = startTime - now;
      const diffToStartMins = Math.max(1, Math.ceil(diffToStartMs / (60 * 1000)));
      return {
        canJoin: true,
        label: "Join Live Session",
        reason: `Session starts in ${diffToStartMins} min. You can join now to settle in.`,
        status: "pre_session",
        minutesUntilStart: diffToStartMins,
      };
    }
    return {
      canJoin: true,
      label: "Join Live Session",
      reason: "Session is live! Click to join now.",
      status: "open",
      minutesUntilStart: 0,
    };
  } else {
    return {
      canJoin: false,
      label: "Session Concluded",
      reason: "This live session has already ended.",
      status: "ended",
    };
  }
}

function getCancellationStatus(rawDate, durationMinutes = 30) {
  if (!rawDate) return { canCancel: true, reason: "" };
  const d = parseUtcDate(rawDate);
  if (!d || isNaN(d.getTime())) return { canCancel: true, reason: "" };

  const now = Date.now();
  const startTime = d.getTime();
  const cutoffTime = startTime - 60 * 60 * 1000; // 1 hour before
  const endTime = startTime + (durationMinutes + 15) * 60 * 1000;

  if (now >= cutoffTime && now <= endTime) {
    return {
      canCancel: false,
      reason: "Cancellation is closed within 1 hour of the session start time.",
    };
  }
  return {
    canCancel: true,
    reason: "",
  };
}

function formatEventDisplayDate(rawDate, durationMinutes = 30) {
  if (!rawDate) {
    return {
      hostDate: "",
      hostFullDisplay: "",
      userDate: "",
      userFullDisplay: "",
      userTz: "UTC",
      isDifferentTimezone: false,
      calendarDate: "2026-01-01",
      startTime: "12:00",
      endTime: "12:30",
    };
  }

  const d = parseUtcDate(rawDate);
  const endD = new Date(d.getTime() + durationMinutes * 60 * 1000);

  // User's detected timezone
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const hostTz = "America/Toronto";

  // Formatters for Host (Canada / Eastern Time)
  const hostDateFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: hostTz,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const hostTimeFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: hostTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const hostTzFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: hostTz,
    timeZoneName: "short",
  });

  // Formatters for Attendee Local Time
  const userDateFmt = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const userTimeFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const userTzFmt = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
  });

  const hostDate = hostDateFmt.format(d);
  const hostStart = hostTimeFmt.format(d);
  const hostEnd = hostTimeFmt.format(endD);
  const hostTzShort =
    hostTzFmt.formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "EDT";

  const userDate = userDateFmt.format(d);
  const userStart = userTimeFmt.format(d);
  const userEnd = userTimeFmt.format(endD);
  const userTzShort =
    userTzFmt.formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "";

  const hostFullDisplay = `${hostDate} • ${hostStart} – ${hostEnd} ${hostTzShort}`;
  const userFullDisplay = `${userDate} • ${userStart} – ${userEnd} ${userTzShort ? `(${userTzShort})` : ""}`;

  // Check if timezone or formatted time differs
  const isDifferentTimezone =
    hostDate !== userDate || hostStart !== userStart || hostTzShort !== userTzShort;

  // For add-to-calendar
  const userYear = d.getFullYear();
  const userMonth = String(d.getMonth() + 1).padStart(2, "0");
  const userDay = String(d.getDate()).padStart(2, "0");
  const calendarDate = `${userYear}-${userMonth}-${userDay}`;

  const to24Hr = (dateObj) => {
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const startTime = to24Hr(d);
  const endTime = to24Hr(endD);

  return {
    hostDate,
    hostStart,
    hostEnd,
    hostTzShort,
    hostFullDisplay,
    userDate,
    userStart,
    userEnd,
    userTzShort,
    userFullDisplay,
    userTz,
    isDifferentTimezone,
    calendarDate,
    startTime,
    endTime,
  };
}

export const LiveRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [unregistering, setUnregistering] = useState(false);

  const { ID } = useParams();

  const loadEvent = useCallback(async () => {
    if (!ID) return;
    setLoading(true);
    try {
      const data = await getLiveEventById(ID);
      setEventData(data);
    } catch (err) {
      console.error("Failed to load event:", err);
      toast.error("Could not load event details.");
    } finally {
      setLoading(false);
    }
  }, [ID]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const handleRegister = async () => {
    if (!ID || registering || unregistering) return;
    setRegistering(true);
    try {
      const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";
      const res = await registerForLiveEvent(ID, undefined, userTz);
      if (res.result === 1) {
        toast.success("You have successfully registered.");
        toast.info("A confirmation email has been sent to your address.");
        dispatch(updateEventRegistration({ eventId: ID, isRegistered: true }));
      } else if (res.result === 3) {
        toast.error("You are already registered for this event.");
      } else {
        toast.error(res.message || "Unfortunately, this event is full.");
      }
    } catch (err) {
      toast.error("Something went wrong. Your registration was not successful.");
      console.error(err);
    } finally {
      setRegistering(false);
      loadEvent();
    }
  };

  const handleUnregister = async () => {
    if (!ID || unregistering || registering) return;
    setUnregistering(true);
    try {
      const res = await unregisterFromLiveEvent(ID);
      if (res.result === 1) {
        toast.info("You have been removed from this event's registration list.");
        dispatch(updateEventRegistration({ eventId: ID, isRegistered: false }));
      } else {
        toast.error(res.message || "Failed to unregister.");
      }
    } catch (err) {
      toast.error("Failed to unregister.");
      console.error(err);
    } finally {
      setUnregistering(false);
      loadEvent();
    }
  };

  const [, setTick] = useState(0);
  useEffect(() => {
    // Re-check 30-minute join window every 20 seconds
    const interval = setInterval(() => setTick((t) => t + 1), 20000);
    return () => clearInterval(interval);
  }, []);

  const timeInfo = formatEventDisplayDate(
    eventData?.event_date,
    eventData?.duration_minutes || 30
  );

  const joinStatus = getJoinWindowStatus(
    eventData?.event_date,
    eventData?.duration_minutes || 30
  );

  const cancelStatus = getCancellationStatus(
    eventData?.event_date,
    eventData?.duration_minutes || 30
  );

  const capacity = eventData?.capacity || 100;
  const filled = eventData?.filled_count || 0;
  const fillPercentage = Math.min(100, Math.round((filled / capacity) * 100));

  return (
    <div className="LiveRegisterPage">
      <div className="sidebarLive">
        <SidebarCol />
      </div>

      <div className="LiveRegisterMainContent">
        <MainHeader
          page="Live Events"
          titleText="Live Group Guidance"
          subtitleText={eventData?.title ? `Session: ${eventData.title}` : "Interactive Guided Mindfulness"}
          back="/live"
        />

        <div className="LiveRegisterBreadcrumb">
          <span className="BreadcrumbLink" onClick={() => navigate("/home")}>
            Home
          </span>
          <span className="BreadcrumbDivider">/</span>
          <span className="BreadcrumbLink" onClick={() => navigate("/live")}>
            Live Sessions
          </span>
          <span className="BreadcrumbDivider">/</span>
          <span className="BreadcrumbCurrent">{eventData?.title || "Session Details"}</span>
        </div>

        {loading && (
          <div className="LiveRegisterLoadingBox">
            <div className="LiveEventsMainSpinner"></div>
            <p>Loading session details...</p>
          </div>
        )}

        {!loading && eventData && (
          <div className="LiveRegisterGrid">
            {/* Left Column: Event Overview Card */}
            <div className="EventOverviewCard">
              <div className="EventBannerContainer">
                <img
                  src={eventData.cover_image || defaultImage}
                  alt={eventData.title}
                  className="EventBannerImage"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultImage;
                  }}
                />
                <span className="EventCategoryBadge">
                  {eventData.category || "General"}
                </span>
              </div>

              <div className="EventContentBody">
                <div className="EventTitleRow">
                  <h1 className="EventMainTitle">{eventData.title}</h1>
                  <button className="EventBookmarkButton" type="button" title="Bookmark Session">
                    <FontAwesomeIcon icon={faBookmark} />
                  </button>
                </div>

                <div className="EventPresenterCard">
                  <img
                    src={eventData.presenter_avatar || defaultImage}
                    alt={eventData.presenter}
                    className="PresenterAvatarImg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />
                  <div className="PresenterMeta">
                    <span className="PresenterRoleLabel">SESSION GUIDE</span>
                    <span className="PresenterFullName">
                      {eventData.presenter || "MindSherpa Presenter"}
                    </span>
                  </div>
                </div>

                <div className="EventDescriptionBlock">
                  <h3 className="DescriptionHeading">About This Guidance</h3>
                  <p className="DescriptionParagraph">{eventData.description}</p>
                </div>
              </div>
            </div>

            {/* Right Column: Registration & Session Status */}
            <div className="EventRegistrationColumn">
              <div className="RegistrationSummaryCard">
                <h2 className="SummaryCardHeading">Registration & Details</h2>

                {eventData.is_registered && (
                  <div className="RegisteredBanner">
                    <FontAwesomeIcon icon={faCheckCircle} className="RegisteredBannerIcon" />
                    <span>You are registered for this session</span>
                  </div>
                )}

                <div className="SummarySection DateTimeSection">
                  <div className="SummarySectionHeader">
                    <FontAwesomeIcon icon={faCalendar} className="SectionHeaderIcon" />
                    <span className="SectionHeaderTitle">Date & Time</span>
                  </div>

                  <div className="TimezoneDisplayContainer">
                    {/* Attendee Local Time (Primary) */}
                    <div className="LocalTimeCard">
                      <div className="LocalTimeCardHeader">
                        <span className="LocalTimeBadge">
                          <FontAwesomeIcon icon={faClock} style={{ marginRight: 6 }} />
                          Your Local Time
                        </span>
                      </div>
                      <p className="LocalTimePrimary">{timeInfo.userFullDisplay}</p>
                      
                      {timeInfo.isDifferentTimezone && (
                        <div className="LocalTimeNotice">
                          <span className="LocalTimeNoticeIcon">💡</span>
                          <span className="LocalTimeNoticeText">
                            <strong>This is your local time zone.</strong> You should join the meeting at this time.
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Host Time (Canada / Eastern Time) */}
                    {timeInfo.isDifferentTimezone && (
                      <div className="HostTimeCard">
                        <div className="HostTimeHeader">
                          <span className="HostFlag">🇨🇦</span>
                          <span className="HostTimeLabel">Host Time (Canada / Eastern)</span>
                        </div>
                        <p className="HostTimeValue">{timeInfo.hostFullDisplay}</p>
                      </div>
                    )}

                    <div className="CalendarIntegration">
                      <add-to-calendar-button
                        name={`MindSherpa Live Session - ${eventData.title}`}
                        startDate={timeInfo.calendarDate}
                        startTime={timeInfo.startTime}
                        endTime={timeInfo.endTime}
                        location={`${window.location.origin}/live/join/${ID}`}
                        options="['Google','Apple','Outlook.com']"
                        timeZone={timeInfo.userTz}
                      />
                    </div>
                  </div>
                </div>

                <div className="SummaryCardDivider" />

                <div className="SummarySection">
                  <div className="SummarySectionHeader">
                    <FontAwesomeIcon icon={faUsers} className="SectionHeaderIcon" />
                    <span className="SectionHeaderTitle">Attendees & Capacity</span>
                  </div>
                  <div className="CapacityStatRow">
                    <span className="CapacityCount">
                      <strong>{filled}</strong> of {capacity} seats filled
                    </span>
                    <span className="CapacityBadge">{fillPercentage}% Full</span>
                  </div>
                  <div className="CapacityBarTrack">
                    <div
                      className="CapacityBarFill"
                      style={{ width: `${fillPercentage}%` }}
                    />
                  </div>
                </div>

                <div className="SummaryCardDivider" />

                <div className="SummarySection">
                  <div className="SummarySectionHeader">
                    <FontAwesomeIcon icon={faLocationDot} className="SectionHeaderIcon" />
                    <span className="SectionHeaderTitle">Location & Stream</span>
                  </div>
                  <p className="SummaryTextBold">Live Stream Gateway</p>
                  <div className="SecurityHint">
                    <FontAwesomeIcon icon={faShieldHalved} className="SecurityIcon" />
                    <span>Meeting link is secured and validated upon joining.</span>
                  </div>
                </div>

                <div className="SummaryActionArea">
                  {eventData.is_registered ? (
                    <div className="ActionBtnGroup">
                      {joinStatus.status === "pre_session" && (
                        <div
                          style={{
                            backgroundColor: "#EFF6FF",
                            borderLeft: "4px solid #3B82F6",
                            borderRadius: "6px",
                            padding: "10px 14px",
                            marginBottom: "12px",
                            fontSize: "13px",
                            color: "#1E40AF",
                            lineHeight: 1.4,
                          }}
                        >
                          <FontAwesomeIcon icon={faClock} style={{ marginRight: 6 }} />
                          <span>
                            Meeting will start in <strong>{joinStatus.minutesUntilStart} min</strong>. You can join now to settle in.
                          </span>
                        </div>
                      )}

                      <button
                        type="button"
                        className={`BtnJoinLiveSession ${!joinStatus.canJoin ? "disabledJoin" : ""}`}
                        disabled={!joinStatus.canJoin}
                        onClick={() => {
                          if (joinStatus.canJoin) {
                            window.open(`/live/join/${ID}`, "_blank");
                          }
                        }}
                      >
                        <FontAwesomeIcon
                          icon={joinStatus.canJoin ? faVideo : faLock}
                          style={{ marginRight: 8 }}
                        />
                        {joinStatus.label}
                      </button>

                      {!joinStatus.canJoin && (
                        <div className="JoinWindowNotice">
                          <FontAwesomeIcon icon={faClock} className="JoinWindowNoticeIcon" />
                          <span>{joinStatus.reason}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className={`BtnUnregister ${!cancelStatus.canCancel ? "disabledUnregister" : ""}`}
                        disabled={unregistering || registering || !cancelStatus.canCancel}
                        title={!cancelStatus.canCancel ? cancelStatus.reason : ""}
                        style={
                          !cancelStatus.canCancel
                            ? { opacity: 0.6, cursor: "not-allowed", backgroundColor: "#F1F5F9", color: "#94A3B8", borderColor: "#CBD5E1" }
                            : {}
                        }
                        onClick={handleUnregister}
                      >
                        {unregistering ? (
                          <>
                            <span className="btnSpinner btnSpinnerDark"></span>
                            Cancelling...
                          </>
                        ) : !cancelStatus.canCancel ? (
                          "Cancellation Closed (<1h)"
                        ) : (
                          "Cancel Registration"
                        )}
                      </button>

                      {!cancelStatus.canCancel && (
                        <span style={{ fontSize: "12px", color: "#64748B", textAlign: "center", display: "block", marginTop: "2px" }}>
                          Registration cannot be cancelled within 1 hour of the session.
                        </span>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="BtnRegisterPrimary"
                      disabled={registering || unregistering || filled >= capacity}
                      onClick={handleRegister}
                    >
                      {registering ? (
                        <>
                          <span className="btnSpinner"></span>
                          Registering...
                        </>
                      ) : filled >= capacity ? (
                        "Session is Full"
                      ) : (
                        "Register for this Session"
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveRegister;
